import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import type { RefObject, ComponentProps } from 'react'

import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react'

import * as React from 'react'
import { useIntl } from 'react-intl'
import FormattedCruiseLineSearchSummary from '../CruiseLineSelect/FormattedCruiseLineSearchSummary'
import type { DeparturePortMapped } from './CruiseDepartureSelect.types'
import CruiseDepartureSelectList from './CruiseDepartureSelectList'
import { useCruiseSearchInput } from '../CruiseSearchInputContext'
import FilterSearchInput from 'components/Common/FilterPanel/FilterSearchInput'
import CruiseFilterInput from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseFilterInput'
import { trackClientEvent, trackEvent } from 'analytics/analytics'
import { mapCruiseLineFilterToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import { searchEventWithContext } from 'analytics/snowplow/events'
import ChipMultiSelectInput from 'components/Common/Form/Input/ChipMultiSelectInput'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'

const SET_SECONDARY_SEARCH_ITEMS = GlobalSearchStateActions.SET_SECONDARY_SEARCH_ITEMS

export interface CruiseDepartureSelectDesktopRef {
  focus: () => void
}

interface Props {
  onChange: (values: App.CruiseGlobalFilters) => void
  dropdownAnchorRef: RefObject<HTMLElement | null>
  regions: Array<App.CruiseDepartureRegion>
  setIsAllSelected: (value: boolean) => void
  isAllSelected: boolean
  setSearchTerm: (value: string) => void
  searchTerm: string
  setSelectedRegion: (value: App.CruiseDepartureRegion) => void
  selectedRegion: App.CruiseDepartureRegion
  handleSearchTerm: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleOnChange: (e: Array<App.SearchItem>) => void
  searchDeparturePorts: Array<DeparturePortMapped>
  selectedRegions: Array<string>
  setSelectedRegions: (value: Array<string>) => void
  onRemoveSelectedOption: (value: string) => void
  fetching: boolean
  isChipMode?: boolean
  dropdownSize?: ComponentProps<typeof DropdownSheet>['size']
  shouldDisableRegionTabs?: boolean
  initialFilters?: App.CruiseInitialFilters
}

const CruiseDepartureSelectDesktop = forwardRef<CruiseDepartureSelectDesktopRef, Props>(({
  regions,
  onChange,
  dropdownAnchorRef,
  setIsAllSelected,
  isAllSelected,
  setSearchTerm,
  searchTerm,
  setSelectedRegion,
  selectedRegion,
  handleSearchTerm,
  handleOnChange,
  searchDeparturePorts,
  selectedRegions,
  setSelectedRegions,
  onRemoveSelectedOption,
  fetching,
  isChipMode,
  dropdownSize,
  shouldDisableRegionTabs,
  initialFilters,
}, ref) => {
  const intl = useIntl()
  const globalSearchState = useContext(GlobalSearchStateContext)
  const { secondarySearchItems } = globalSearchState
  const searchDispatch = useContext(GlobalSearchDispatchContext)

  const { isDesktopOpen, open, close } = useCruiseSearchInput()

  const handleOnApply = useCallback(() => {
    onChange({ secondarySearchItems })
    setSearchTerm('')
    close()
  }, [onChange, secondarySearchItems, close, setSearchTerm])

  const handleOnClear = useCallback(() => {
    searchDispatch({
      type: SET_SECONDARY_SEARCH_ITEMS,
      secondarySearchItems: [],
    })
    onChange({ secondarySearchItems: [] })
    setIsAllSelected(false)
    setSelectedRegions([])
    setSearchTerm('')
  }, [onChange, searchDispatch, setIsAllSelected, setSearchTerm, setSelectedRegions])

  useEffect(() => {
    if (!isDesktopOpen && regions[0]) {
      setSelectedRegion(regions[0])
    }
  }, [regions, setSelectedRegion, isDesktopOpen])

  const inputValue = useMemo(() => isDesktopOpen ? searchTerm : '', [isDesktopOpen, searchTerm])
  const shouldShowChipInput = isDesktopOpen

  const selectedOptions = useMemo(() => secondarySearchItems.map((item) => ({
    value: item.value,
    label: item.format.mainText,
  })), [secondarySearchItems])

  const selectedSummary = useMemo(() => {
    if (!selectedOptions.length) {
      return ''
    }

    const remainingSelectedOptions = selectedOptions.length - 1
    return remainingSelectedOptions ? `${selectedOptions[0].label} + ${remainingSelectedOptions}` : selectedOptions[0].label
  }, [selectedOptions])

  const handlerClearSearchTerm = useCallback(() => setSearchTerm(''), [setSearchTerm])
  const handleClose = useCallback(() => {
    setSearchTerm('')
    close()
  }, [close, setSearchTerm])
  const inputTriggerRef = useRef<HTMLInputElement>(null)

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputTriggerRef.current?.focus()
      open()
    },
  }), [open])

  const departingFromLabel = intl.formatMessage({
    id: 'DEnpcloyPc_dk_gOiBh4',
    defaultMessage: 'Departing from',
    description: 'Label for cruise departure port filter in the cruise offer or booking section',
  })

  const searchDepartingFromPlaceholder = intl.formatMessage({
    id: 'N5nPB-J4sJV1-Mdfb8gk',
    defaultMessage: 'Search departing from',
    description: 'Placeholder text when searching for departure ports',
  })

  const allDeparturePortsPlaceholder = intl.formatMessage({
    id: 'w4vyvXfeeK4IN36vN05F',
    defaultMessage: 'All departure ports',
    description: 'Placeholder text showing all departure ports are selected',
  })

  const searchResultsSubtitle = useMemo(() => {
    if (searchTerm && !isChipMode) {
      return intl.formatMessage({
        id: 'kfaravFAR_c29CDTNeDx',
        defaultMessage: 'Search results for "{searchTerm}"',
        description: 'Subtitle showing search results for departure port search term',
      }, { searchTerm })
    }
    return undefined
  }, [searchTerm, isChipMode, intl])

  return <div data-testid="cruise-departure-select-desktop">
    {isChipMode && (
      <CruiseFilterInput
        isOpen={isDesktopOpen}
        label={departingFromLabel}
        placeholder={isDesktopOpen ? searchDepartingFromPlaceholder : allDeparturePortsPlaceholder}
        inputValue={inputValue}
        data-testid="cruise-departure-select-desktop-input"
        formFieldRef={inputTriggerRef}
        selectedItems={secondarySearchItems.length}
        onClick={() => {
          trackClientEvent({
            subject: 'filter_opened',
            action: 'opened',
            category: 'cruise_departure_filter',
            type: 'interaction',
          })
          open()
        }}
        handleSearchTerm={handleSearchTerm}
        handlerClearSearchTerm={handlerClearSearchTerm}
        isChipMode={isChipMode}
      />
    )}

    {!isChipMode && !shouldShowChipInput && (
      <CruiseFilterInput
        isOpen={isDesktopOpen}
        label={departingFromLabel}
        placeholder={allDeparturePortsPlaceholder}
        inputValue={selectedSummary}
        data-testid="cruise-departure-select-desktop-input"
        formFieldRef={inputTriggerRef}
        selectedItems={secondarySearchItems.length}
        onClick={() => {
          trackClientEvent({
            subject: 'filter_opened',
            action: 'opened',
            category: 'cruise_departure_filter',
            type: 'interaction',
          })
          open()
        }}
        handleSearchTerm={handleSearchTerm}
        handlerClearSearchTerm={handlerClearSearchTerm}
      />
    )}

    {!isChipMode && shouldShowChipInput && (
      <ChipMultiSelectInput
        ref={inputTriggerRef}
        data-testid="cruise-departure-select-desktop-input"
        selectedOptions={selectedOptions}
        expanded
        placeholder={isDesktopOpen ? searchDepartingFromPlaceholder : allDeparturePortsPlaceholder}
        value={inputValue}
        startIcon={<LineSearchIcon />}
        onClick={() => {
          trackClientEvent({
            subject: 'filter_opened',
            action: 'opened',
            category: 'cruise_departure_filter',
            type: 'interaction',
          })
          open()
        }}
        onChange={handleSearchTerm}
        onRemoveOption={onRemoveSelectedOption}
      />
    )}

    <DropdownSheet
      data-testid="cruise-departure-select-desktop-dropdown"
      size={dropdownSize || 'fill-anchor'}
      anchorRef={dropdownAnchorRef}
      triggerRef={inputTriggerRef}
      open={isDesktopOpen}
      onClose={handleClose}
      subtitle={searchResultsSubtitle}
      title={isChipMode ? departingFromLabel : undefined}
      hasDismissButton={isChipMode}
      headerExtension={<>
        {isChipMode && <FilterSearchInput
          placeholder={intl.formatMessage({
            id: '4l1e3r9rb6LHTvqAB4wW',
            defaultMessage: 'Search for departures',
            description: 'Placeholder text for searching departure ports in chip mode',
          })}
          onChange={setSearchTerm}
          value={inputValue}
        />}

        {!searchTerm && !shouldDisableRegionTabs && <TabButtonsGroup data-testid="cruise-departure-select-content-tab-group">
          {regions.map((region) => (
            <TabButton
              key={region.identifier}
              size="medium"
              isActive={region.identifier === selectedRegion.identifier}
              onClick={() => {
                trackClientEvent({
                  subject: 'filter_region_selected',
                  action: 'selected',
                  category: 'cruise_departure_filter',
                  type: 'interaction',
                })
                setSelectedRegion(region)
              }}
            >
              {region.name}
            </TabButton>
          ))}
        </TabButtonsGroup>}
      </>}
      primaryActionProps={{
        'data-testid': 'cruise-filter-footer-apply',
        children: intl.formatMessage({
          id: 'sWpTAGOfv-VlyomL8PMk',
          defaultMessage: 'Apply',
          description: 'Apply button text for cruise filters in the cruise offer or booking section',
        }),
        onClick: () => {
          trackClientEvent({
            subject: 'filter_applied',
            action: 'applied',
            category: 'cruise_departure_filter',
            type: 'interaction',
          })

          trackEvent(searchEventWithContext({
            searchEvent: { type: 'filters' },
            sortContext: { value: 'recommended' },
            searchContext: mapCruiseLineFilterToSnowplowSearchEvent(globalSearchState),
            filterContexts: secondarySearchItems.map(item => ({
              label: 'Departure Port',
              type: 'departure_port',
              value: item.value,
            })),
          }))
          handleOnApply()
        },
      }}
      secondaryActionProps={{
        'data-testid': 'cruise-filter-footer-clear-all',
        kind: 'tertiary',
        children: intl.formatMessage({
          id: 'gJO-i9sIzK5zcA4uhdPj',
          defaultMessage: 'Reset all',
          description: 'Reset all button text for cruise filters in the cruise section',
        }),
        onClick: () => {
          trackClientEvent({
            subject: 'filter_cleared',
            action: 'cleared',
            category: 'cruise_departure_filter',
            type: 'interaction',
          })
          handleOnClear()
        },
      }}
      footerStart={!isChipMode && <FormattedCruiseLineSearchSummary
        initialFilters={initialFilters}
      />}
    >
      <CruiseDepartureSelectList
        searchDeparturePorts={searchDeparturePorts}
        searchTerm={searchTerm}
        secondarySearchItems={secondarySearchItems}
        setIsAllSelected={setIsAllSelected}
        isAllSelected={isAllSelected}
        onChange={handleOnChange}
        setSelectedRegions={setSelectedRegions}
        fetching={fetching}
        selectedRegion={selectedRegion}
        selectedRegions={selectedRegions}
        setSelectedRegion={setSelectedRegion}
        regions={regions}
        shouldDisableRegionTabs={shouldDisableRegionTabs}
      />
    </DropdownSheet>
  </div>
})

CruiseDepartureSelectDesktop.displayName = 'CruiseDepartureSelectDesktop'

export default CruiseDepartureSelectDesktop
